import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Encounter, LabRequest, RadiologyRequest } from '../../types';
import { Search, FileText, FlaskConical, Scan, Printer, Download, X } from 'lucide-react';
import { fbcStructure, urinalysisTests, testosteroneTests, hormonalProfileTests, amhStructure, isFBCTest, isUrinalysisTest, isTestosteroneTest, isHormonalProfileTest, isAMHTest } from '../../store/labStructures';
import jsPDF from 'jspdf';
interface Props {}
export const PatientReports: React.FC<Props> = () => {
  const {
    encounters,
    patients
  } = useData();
  const [searchQuery, setSearchQuery] = useState('');
  const [viewing, setViewing] = useState<{
    encounter: Encounter;
  } | null>(null);
  // Encounters that have at least one completed lab OR a radiology report
  const reportEncounters = useMemo(() => {
    return encounters.filter(e => {
      const hasCompletedLab = e.labRequests.some(l => l.status === 'Completed');
      const hasRadiologyReport = e.radiologyRequests.some(r => r.report || r.images && r.images.length > 0);
      return hasCompletedLab || hasRadiologyReport;
    }).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  }, [encounters]);
  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return reportEncounters;
    return reportEncounters.filter(e => {
      const p = patients.find(pt => pt.id === e.patientId);
      if (!p) return false;
      return p.firstName.toLowerCase().includes(q) || p.lastName.toLowerCase().includes(q) || p.bmcFileNumber.toLowerCase().includes(q);
    });
  }, [reportEncounters, patients, searchQuery]);
  const getPatient = (id: string) => patients.find(p => p.id === id);
  const handlePrint = () => window.print();
  const handleDownloadPDF = () => {
    if (!viewing) return;
    const patient = getPatient(viewing.encounter.patientId);
    if (!patient) return;
    const pdf = new jsPDF({
      unit: 'pt',
      format: 'a4'
    });
    const margin = 40;
    let y = margin;
    pdf.setFontSize(18);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Patient Report', margin, y);
    y += 22;
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'normal');
    pdf.text(`${patient.firstName} ${patient.lastName} · BMC ${patient.bmcFileNumber} · ${patient.age} yrs · ${patient.sex}`, margin, y);
    y += 14;
    pdf.text(`Visit date: ${new Date(viewing.encounter.startedAt).toLocaleString()}`, margin, y);
    y += 20;
    const lineCheck = () => {
      const ph = pdf.internal.pageSize.getHeight() - margin;
      if (y > ph) {
        pdf.addPage();
        y = margin;
      }
    };
    // ── Laboratory results ──
    const completedLabs = viewing.encounter.labRequests.filter(l => l.status === 'Completed');
    if (completedLabs.length > 0) {
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Laboratory Results', margin, y);
      y += 16;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      completedLabs.forEach(lab => {
        lineCheck();
        pdf.setFont('helvetica', 'bold');
        const activeTests = lab.tests.filter(t => !(lab.removedTestNames || []).includes(t));
        pdf.text(`Tests: ${activeTests.join(', ')}`, margin, y);
        y += 12;
        pdf.setFont('helvetica', 'normal');
        if (lab.completedAt) {
          pdf.text(`Completed: ${new Date(lab.completedAt).toLocaleString()}`, margin, y);
          y += 12;
        }
        if (lab.results) {
          Object.entries(lab.results).forEach(([k, v]) => {
            if (!v) return;
            lineCheck();
            const line = `  ${k}: ${v}`;
            const lines = pdf.splitTextToSize(line, 515);
            pdf.text(lines, margin, y);
            y += lines.length * 11;
          });
        }
        if (lab.otherTests) {
          lineCheck();
          pdf.text(`  Other: ${lab.otherTests}`, margin, y);
          y += 12;
        }
        if (lab.labComments) {
          lineCheck();
          pdf.setFont('helvetica', 'bold');
          pdf.text('Comments / Observations:', margin, y);
          y += 12;
          pdf.setFont('helvetica', 'normal');
          const lines = pdf.splitTextToSize(lab.labComments, 515);
          lines.forEach((line: string) => {
            lineCheck();
            pdf.text(line, margin, y);
            y += 11;
          });
        }
        y += 6;
      });
    }
    // ── Radiology results ──
    const radWithReport = viewing.encounter.radiologyRequests.filter(r => r.report);
    if (radWithReport.length > 0) {
      lineCheck();
      pdf.setFontSize(13);
      pdf.setFont('helvetica', 'bold');
      pdf.text('Radiology Reports', margin, y);
      y += 16;
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      radWithReport.forEach(r => {
        lineCheck();
        pdf.setFont('helvetica', 'bold');
        pdf.text(`Scan: ${r.scanType}`, margin, y);
        y += 12;
        pdf.setFont('helvetica', 'normal');
        if (r.completedAt) {
          pdf.text(`Completed: ${new Date(r.completedAt).toLocaleString()}`, margin, y);
          y += 12;
        }
        const lines = pdf.splitTextToSize(r.report || '', 515);
        lines.forEach((line: string) => {
          lineCheck();
          pdf.text(line, margin, y);
          y += 11;
        });
        y += 8;
      });
    }
    pdf.save(`report-${patient.bmcFileNumber}-${new Date(viewing.encounter.startedAt).toISOString().slice(0, 10)}.pdf`);
  };
  // ── Renderers for the View modal ──
  const renderLabResults = (lab: LabRequest) => {
    if (!lab.results) return null;
    const activeTests = lab.tests.filter(t => !(lab.removedTestNames || []).includes(t));
    const focusedTest = activeTests[0];
    if (isFBCTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-3510">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-3511">
            FBC Report
          </h4>
          {fbcStructure.map(cat => <div key={cat.category} className="mb-3" data-id="element-3512">
              <p className="font-semibold text-gray-700 mb-1" data-id="element-3513">{cat.category}</p>
              <table className="w-full text-xs border border-gray-300" data-id="element-3514">
                <thead className="bg-gray-100" data-id="element-3515">
                  <tr data-id="element-3516">
                    <th className="border border-gray-300 p-1.5 text-left" data-id="element-3517">
                      Test
                    </th>
                    <th className="border border-gray-300 p-1.5 text-left w-24" data-id="element-3518">
                      Result
                    </th>
                    <th className="border border-gray-300 p-1.5 text-left" data-id="element-3519">
                      Reference
                    </th>
                    <th className="border border-gray-300 p-1.5 text-left w-20" data-id="element-3520">
                      Unit
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-3521">
                  {cat.tests.map(t => <tr key={t.name} data-id="element-3522">
                      <td className="border border-gray-300 p-1.5" data-id="element-3523">{t.name}</td>
                      <td className="border border-gray-300 p-1.5 font-bold" data-id="element-3524">
                        {lab.results![t.name] || '—'}
                      </td>
                      <td className="border border-gray-300 p-1.5 text-xs" data-id="element-3525">
                        {t.range}
                      </td>
                      <td className="border border-gray-300 p-1.5 text-xs" data-id="element-3526">
                        {t.unit}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>)}
        </div>;
    }
    if (isUrinalysisTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-3527">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-3528">
            Urinalysis Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-3529">
            <thead className="bg-gray-100" data-id="element-3530">
              <tr data-id="element-3531">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-3532">
                  Parameter
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-32" data-id="element-3533">
                  Result
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-3534">
                  Reference
                </th>
              </tr>
            </thead>
            <tbody data-id="element-3535">
              {urinalysisTests.map(t => <tr key={t.name} data-id="element-3536">
                  <td className="border border-gray-300 p-1.5" data-id="element-3537">{t.name}</td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-3538">
                    {lab.results![`UA_${t.name}`] || '—'}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-3539">
                    {t.normal || '—'}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>;
    }
    if (isTestosteroneTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-3540">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-3541">
            Testosterone Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-3542">
            <thead className="bg-gray-100" data-id="element-3543">
              <tr data-id="element-3544">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-3545">Test</th>
                <th className="border border-gray-300 p-1.5 text-left w-28" data-id="element-3546">
                  Result
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-3547">
                  Reference (M)
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-3548">
                  Reference (F)
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-16" data-id="element-3549">
                  Unit
                </th>
              </tr>
            </thead>
            <tbody data-id="element-3550">
              {testosteroneTests.map(t => <tr key={t.name} data-id="element-3551">
                  <td className="border border-gray-300 p-1.5" data-id="element-3552">{t.name}</td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-3553">
                    {lab.results![`TESTO_${t.name}`] || '—'}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-3554">
                    {t.rangeMale}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-3555">
                    {t.rangeFemale}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-3556">
                    {t.unit}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>;
    }
    if (isAMHTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-3557">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-3558">
            Anti Mullerian Hormone (AMH) Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-3559">
            <thead className="bg-gray-100" data-id="element-3560">
              <tr data-id="element-3561">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-3562">Test</th>
                <th className="border border-gray-300 p-1.5 text-left w-28" data-id="element-3563">
                  Value
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-3564">
                  Reference (by age)
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-16" data-id="element-3565">
                  Unit
                </th>
              </tr>
            </thead>
            <tbody data-id="element-3566">
              <tr className="align-top" data-id="element-3567">
                <td className="border border-gray-300 p-1.5 font-medium" data-id="element-3568">
                  {amhStructure.name}
                </td>
                <td className="border border-gray-300 p-1.5 font-bold" data-id="element-3569">
                  {lab.results![amhStructure.storageKey] || focusedTest && lab.results![focusedTest] || lab.results!['AMH'] || lab.results!['Anti-Müllerian Hormone'] || lab.results!['ANTI-MÜLLERIAN HORMONE'] || lab.results!['Anti Mullerian Hormone (AMH)'] || '—'}
                </td>
                <td className="border border-gray-300 p-1.5 text-xs" data-id="element-3570">
                  {amhStructure.ageRanges.map(a => <div key={a.ageBand} data-id="element-3571">
                      <span className="font-medium" data-id="element-3572">{a.ageBand}:</span>{' '}
                      {a.range}
                    </div>)}
                </td>
                <td className="border border-gray-300 p-1.5 text-xs" data-id="element-3573">
                  {amhStructure.unit}
                </td>
              </tr>
            </tbody>
          </table>
          <p className="mt-2 text-[10px] italic text-gray-600" data-id="element-3574">
            Method: {amhStructure.method}
          </p>
        </div>;
    }
    if (isHormonalProfileTest(focusedTest || '')) {
      return <div className="text-sm" data-id="element-3575">
          <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-3576">
            Hormonal Profile Report
          </h4>
          <table className="w-full text-xs border border-gray-300" data-id="element-3577">
            <thead className="bg-gray-100" data-id="element-3578">
              <tr data-id="element-3579">
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-3580">Test</th>
                <th className="border border-gray-300 p-1.5 text-left w-28" data-id="element-3581">
                  Value
                </th>
                <th className="border border-gray-300 p-1.5 text-left" data-id="element-3582">
                  Reference Range
                </th>
                <th className="border border-gray-300 p-1.5 text-left w-16" data-id="element-3583">
                  Unit
                </th>
              </tr>
            </thead>
            <tbody data-id="element-3584">
              {hormonalProfileTests.map(h => <tr key={h.key} data-id="element-3585">
                  <td className="border border-gray-300 p-1.5 font-medium" data-id="element-3586">
                    {h.name}
                  </td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-3587">
                    {lab.results![`HP_${h.key}`] || '—'}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-3588">
                    {h.ranges.map(r => <div key={r.label} data-id="element-3589">
                        <span className="font-medium" data-id="element-3590">{r.label}:</span>{' '}
                        {r.value}
                      </div>)}
                  </td>
                  <td className="border border-gray-300 p-1.5 text-xs" data-id="element-3591">
                    {h.unit}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>;
    }
    // Fallback: generic key/value table
    return <div className="text-sm" data-id="element-3592">
        <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-3593">
          {activeTests.join(', ')} Report
        </h4>
        <table className="w-full text-xs border border-gray-300" data-id="element-3594">
          <thead className="bg-gray-100" data-id="element-3595">
            <tr data-id="element-3596">
              <th className="border border-gray-300 p-1.5 text-left" data-id="element-3597">
                Parameter
              </th>
              <th className="border border-gray-300 p-1.5 text-left" data-id="element-3598">Result</th>
            </tr>
          </thead>
          <tbody data-id="element-3599">
            {Object.entries(lab.results).map(([k, v]) => v ? <tr key={k} data-id="element-3600">
                  <td className="border border-gray-300 p-1.5" data-id="element-3601">{k}</td>
                  <td className="border border-gray-300 p-1.5 font-bold" data-id="element-3602">
                    {v}
                  </td>
                </tr> : null)}
          </tbody>
        </table>
      </div>;
  };
  const renderRadReport = (r: RadiologyRequest) => <div className="text-sm" data-id="element-3603">
      <h4 className="font-bold uppercase border-b border-black mb-2 pb-1" data-id="element-3604">
        Radiology Report — {r.scanType}
      </h4>
      {r.completedAt && <p className="text-xs text-gray-500 mb-2" data-id="element-3605">
          Completed: {new Date(r.completedAt).toLocaleString()}
        </p>}
      <div className="bg-gray-50 border border-gray-200 rounded p-3 whitespace-pre-wrap text-sm" data-id="element-3606">
        {r.report || 'No report available.'}
      </div>
      {r.images && r.images.length > 0 && <div className="mt-3 grid grid-cols-2 gap-2" data-id="element-3607">
          {r.images.map((img, idx) => <img key={idx} src={img} alt={`${r.scanType} ${idx + 1}`} className="rounded border border-gray-200 object-cover w-full max-h-64" data-id="element-3608" />)}
        </div>}
    </div>;
  return <>
      <Card data-id="element-3609">
        <div className="flex justify-between items-center mb-4 print:hidden" data-id="element-3610">
          <div className="flex items-center gap-2 flex-1 max-w-md" data-id="element-3611">
            <div className="relative w-full" data-id="element-3612">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3613">
                <Search className="h-4 w-4 text-gray-400" data-id="element-3614" />
              </div>
              <input type="text" placeholder="Search by patient name or BMC..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500" data-id="element-3615" />
            </div>
          </div>
          <Badge variant="info" className="text-sm" data-id="element-3616">
            {filtered.length} report{filtered.length === 1 ? '' : 's'}
          </Badge>
        </div>

        {filtered.length === 0 ? <div className="text-center py-12 text-gray-500" data-id="element-3617">
            <FileText className="mx-auto h-12 w-12 text-gray-300 mb-3" data-id="element-3618" />
            <p className="text-lg font-medium text-gray-900" data-id="element-3619">No reports yet</p>
            <p className="text-sm" data-id="element-3620">
              Completed lab and radiology reports will appear here.
            </p>
          </div> : <ul className="divide-y divide-gray-200" data-id="element-3621">
            {filtered.map(enc => {
          const patient = getPatient(enc.patientId);
          if (!patient) return null;
          const completedLabs = enc.labRequests.filter(l => l.status === 'Completed');
          const completedRads = enc.radiologyRequests.filter(r => r.report || r.images && r.images.length > 0);
          return <li key={enc.id} className="py-4 flex justify-between items-start gap-4" data-id="element-3622">
                  <div className="flex-1" data-id="element-3623">
                    <div className="flex items-center gap-2 flex-wrap" data-id="element-3624">
                      <p className="text-base font-bold text-gray-900" data-id="element-3625">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <Badge variant="info" data-id="element-3626">BMC: {patient.bmcFileNumber}</Badge>
                      <span className="text-xs text-gray-500" data-id="element-3627">
                        {patient.age} yrs · {patient.sex}
                      </span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1" data-id="element-3628">
                      Visit: {new Date(enc.startedAt).toLocaleString()}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2" data-id="element-3629">
                      {completedLabs.length > 0 && <span className="inline-flex items-center text-xs bg-yellow-50 text-yellow-800 px-2 py-1 rounded border border-yellow-200" data-id="element-3630">
                          <FlaskConical className="w-3 h-3 mr-1" data-id="element-3631" />
                          {completedLabs.length} Lab Report
                          {completedLabs.length > 1 ? 's' : ''}
                        </span>}
                      {completedRads.length > 0 && <span className="inline-flex items-center text-xs bg-purple-50 text-purple-800 px-2 py-1 rounded border border-purple-200" data-id="element-3632">
                          <Scan className="w-3 h-3 mr-1" data-id="element-3633" />
                          {completedRads.length} Radiology Report
                          {completedRads.length > 1 ? 's' : ''}
                        </span>}
                    </div>
                  </div>
                  <Button variant="outline" onClick={() => setViewing({
              encounter: enc
            })} data-id="element-3634">
                    <FileText className="w-4 h-4 mr-2" data-id="element-3635" /> View Report
                  </Button>
                </li>;
        })}
          </ul>}
      </Card>

      {viewing && <div className="fixed inset-0 bg-gray-600 bg-opacity-75 z-50 overflow-y-auto" data-id="element-3636">
          <div className="min-h-full flex items-start justify-center p-4" data-id="element-3637">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[calc(100vh-2rem)] flex flex-col my-4" data-id="element-3638">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center flex-shrink-0 sticky top-0 bg-white z-10 print:hidden" data-id="element-3639">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-3640">
                  Patient Report
                </h3>
                <div className="flex items-center gap-2" data-id="element-3641">
                  <Button variant="outline" onClick={handlePrint} data-id="element-3642">
                    <Printer className="w-4 h-4 mr-2" data-id="element-3643" /> Print
                  </Button>
                  <Button variant="outline" onClick={handleDownloadPDF} data-id="element-3644">
                    <Download className="w-4 h-4 mr-2" data-id="element-3645" /> Download PDF
                  </Button>
                  <button onClick={() => setViewing(null)} data-id="element-3646">
                    <X className="w-5 h-5 text-gray-400" data-id="element-3647" />
                  </button>
                </div>
              </div>
              <div id="report-printable" className="p-6 overflow-y-auto flex-1" data-id="element-3648">
                {(() => {
              const patient = getPatient(viewing.encounter.patientId);
              if (!patient) return null;
              return <>
                      <div className="border-b-2 border-black pb-3 mb-4" data-id="element-3649">
                        <h2 className="text-xl font-bold uppercase" data-id="element-3650">
                          Patient Report
                        </h2>
                        <div className="grid grid-cols-2 gap-2 mt-2 text-sm" data-id="element-3651">
                          <p data-id="element-3652">
                            <span className="font-semibold" data-id="element-3653">Patient:</span>{' '}
                            {patient.firstName} {patient.lastName}
                          </p>
                          <p data-id="element-3654">
                            <span className="font-semibold" data-id="element-3655">BMC #:</span>{' '}
                            {patient.bmcFileNumber}
                          </p>
                          <p data-id="element-3656">
                            <span className="font-semibold" data-id="element-3657">Age:</span>{' '}
                            {patient.age} yrs
                          </p>
                          <p data-id="element-3658">
                            <span className="font-semibold" data-id="element-3659">Sex:</span>{' '}
                            {patient.sex}
                          </p>
                          <p className="col-span-2" data-id="element-3660">
                            <span className="font-semibold" data-id="element-3661">Visit date:</span>{' '}
                            {new Date(viewing.encounter.startedAt).toLocaleString()}
                          </p>
                        </div>
                      </div>

                      {viewing.encounter.labRequests.filter(l => l.status === 'Completed').map(lab => <div key={lab.id} className="mb-6" data-id="element-3662">
                            {renderLabResults(lab)}
                            {lab.labComments && <div className="mt-4 border border-gray-300 bg-gray-50 rounded p-3" data-id="element-3663">
                                <h5 className="font-bold uppercase mb-1 text-xs" data-id="element-3664">
                                  Comments / Observations
                                </h5>
                                <pre className="whitespace-pre-wrap font-sans text-xs text-gray-800" data-id="element-3665">
                                  {lab.labComments}
                                </pre>
                              </div>}
                          </div>)}

                      {viewing.encounter.radiologyRequests.filter(r => r.report || r.images && r.images.length > 0).map(r => <div key={r.id} className="mb-6" data-id="element-3666">
                            {renderRadReport(r)}
                          </div>)}

                      {viewing.encounter.labRequests.filter(l => l.status === 'Completed').length === 0 && viewing.encounter.radiologyRequests.filter(r => r.report).length === 0 && <p className="text-gray-500 text-center py-8" data-id="element-3667">
                            No reports available for this encounter.
                          </p>}
                    </>;
            })()}
              </div>
            </div>
          </div>
        </div>}

      {/* Print-specific styling */}
      <style data-id="element-3668">{`
        @media print {
          body * { visibility: hidden; }
          #report-printable, #report-printable * { visibility: visible; }
          #report-printable { position: absolute; left: 0; top: 0; width: 100%; padding: 20px; }
        }
      `}</style>
    </>;
};